<template>
  <OptimizationBaseDialog
    v-model="visibility"
    :optimization="props.optimization"
    :person="props.person">
    <div
      v-if="account"
      class="mb-4 flex w-full items-center gap-3 rounded border p-4 hover:cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
      @click="emit('openAccountDrawer', account)">
      <ApplicationIcon
        :application-name="props.optimization.application_display_name"
        class="shrink-0"
        :size="48"
        :application-type="props.optimization.application_type" />
      <div class="flex flex-col">
        <TextMultiline
          :text="props.optimization.application_display_name"
          class="font-bold" />
        <TextMultiline>
          {{ licensesString }}
        </TextMultiline>
        <TextMultiline
          :text="props.person.name ?? props.person.email"
          class="text-gray-400" />
      </div>

      <div class="ml-auto mr-4">
        <SmTooltip :content="i18n.t('showAccount')">
          <ExternalLink :size="24" class="!text-contrast" />
        </SmTooltip>
      </div>
    </div>

    <div
      v-else-if="loading"
      class="group mb-4 flex w-full items-center gap-3 rounded border p-4 hover:cursor-pointer">
      <SmSkeleton height="48px" width="48px" />
      <div class="flex w-1/2 flex-col gap-1">
        <SmSkeleton height="14px" />
        <SmSkeleton height="14px" />
        <SmSkeleton height="14px" width="50%" />
      </div>
    </div>

    <div
      v-else
      class="group mb-4 flex w-full items-center gap-3 rounded border p-4 hover:cursor-pointer">
      <EmptyState
        class="min-h-14"
        :text="i18n.t('noAccountFound')"
        orientation="horizontal" />
    </div>

    <!-- Footer -->
    <template #footer>
      <SmButton
        v-if="props.optimization.hidden && !props.optimization.deprecated"
        outline
        @click="emit('unhide', props.optimization._id)">
        {{ i18n.t('unhideOptimization') }}
      </SmButton>
      <SmButton
        v-else-if="!props.optimization.hidden && !props.optimization.deprecated"
        outline
        @click="emit('hide', props.optimization._id)">
        {{ i18n.t('views.optimizations.table.actions.hideOptimization') }}
      </SmButton>
      <SmButton v-if="!props.optimization.deprecated" @click="executeChanges()">
        {{ i18n.t('deleteLicence') }}
      </SmButton>
    </template>
  </OptimizationBaseDialog>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'

  import {
    OptimizationStore,
    EnrichedOptimizations,
  } from '@/stores/optimization/optimization.store'
  import {
    Account,
    LicenceOut,
    OptimizationSoftwareAccountUnusedData,
    SoftwareLicense,
    SoftwareLicensesService,
  } from '@/client'
  import OptimizationBaseDialog from './OptimizationBaseDialog.vue'
  import { AccountStore } from '@/stores/account/account.store'
  import { sendToast } from '@/components/sm/SmNotification'
  import { ExternalLink } from 'lucide-vue-next'
  import {
    buildSoftwareLicenseMap,
    resolveSoftwareLicenseName,
  } from '@/utils/licenseDisplay'

  const i18n = useI18n()

  const props = defineProps({
    optimization: {
      type: Object as PropType<EnrichedOptimizations>,
      required: true,
    },
    person: {
      type: Object as PropType<Account>,
      required: true,
    },
  })

  const visibility = defineModel({
    type: Boolean,
    required: true,
  })

  const emit = defineEmits(['hide', 'unhide', 'openAccountDrawer'])

  const { data } = props.optimization

  // ----------------------------------------------------------------------
  // Load Data
  // ----------------------------------------------------------------------

  const account = ref<LicenceOut | null>(null)
  const softwareLicensesById = ref<Map<string, SoftwareLicense>>(new Map())
  const loading = ref(false)

  onMounted(async () => {
    loadAccount()
  })

  function loadAccount() {
    loading.value = true
    const info = data as OptimizationSoftwareAccountUnusedData
    AccountStore.getAccountById(info.software_account_id).then((res) => {
      if (res) {
        account.value = res
        loadSoftwareLicenses(Object.keys(res.software_licenses || {}))
      }
      loading.value = false
    })
  }

  function loadSoftwareLicenses(softwareLicenseIds: string[]) {
    if (!softwareLicenseIds.length) {
      softwareLicensesById.value = new Map()
      return
    }

    SoftwareLicensesService.getSoftwareLicensesApiV1SoftwareSoftwareLicensesGet(
      {
        idIn: softwareLicenseIds,
      }
    ).then((softwareLicenses) => {
      softwareLicensesById.value = buildSoftwareLicenseMap(softwareLicenses)
    })
  }

  const licensesString = computed(() => {
    return Object.keys(account.value?.software_licenses || {})
      .map((softwareLicenseId) =>
        resolveSoftwareLicenseName(
          softwareLicenseId,
          softwareLicensesById.value
        )
      )
      .join(', ')
  })

  // ----------------------------------------------------------------------
  // Execute changes
  // ----------------------------------------------------------------------

  const loadingExecute = ref(false)
  function executeChanges() {
    loadingExecute.value = true
    const info = data as OptimizationSoftwareAccountUnusedData

    if (account.value !== null) {
      AccountStore.deleteAccount(info.software_account_id).then((res) => {
        if (res) {
          OptimizationStore.refreshOptimizationCacheFromApi({}, false, false)

          sendToast(i18n.t('AccountDeleteSuccess'), undefined, 'success')
          visibility.value = false
        } else {
          sendToast(i18n.t('AccountDeleteError'), undefined, 'error')
        }
        loadingExecute.value = false
      })
    } else {
      console.error(
        `${i18n.t('error.account.NoAccountException.message')}: ${info.software_account_id}`
      )
      loadingExecute.value = false
    }
  }
</script>
